// src/lib/landingChatEngine.js
//
// Shared chat-engine primitives — prompts, the /api/claude wrapper,
// JSON parsing, and the deterministic fallbacks. Extracted from
// LandingChatFlow.js so both the legacy v5 chat (LandingChatFlow.js,
// still serving the default landing) AND the rescoped landing-v2
// DemoChat (new UI) can call into the same prompt strings + helpers
// without one rewriting the other.
//
// What's NOT here:
//   - The chat state machine (which step / which messages) — that's
//     UI-coupled and stays inside the component.
//   - The sessionStorage persistence key — also UI-coupled (the state
//     shape differs between v5 and v2; they shouldn't share storage).
//   - The brief object construction — happens in the component on
//     pickTitle, after which the brief is handed to onRequireAuth.
//
// Behavior preservation guarantee: this is a pure refactor of
// LandingChatFlow.js. Same prompts, same call shape, same fallbacks.
// The legacy v5 landing must keep working byte-identical to before.

import { modelFor } from './modelConfig';

// ── Prompts ────────────────────────────────────────────────────────────

export const HOOK_PROMPT = (topic, audience) => `You are an A-list podcast producer writing the cold-open HOOK — the 5-10 seconds a listener hears BEFORE the music swells. Your job is to make scrolling fingers stop.

TOPIC: ${topic}
AUDIENCE & ANGLE: ${audience || '(infer the most compelling angle for a curious, smart listener)'}

Write 3 hooks. Each is one sentence, 12-25 words, in the SECOND PERSON or with a concrete first-person claim. Each opens cold — no "Welcome to", no "Today on the show", no "In this episode". Drop the listener INTO a moment.

Pick three different shapes from this list. NEVER reuse a shape across the three:
  • PROVOCATIVE CLAIM — a counter-intuitive truth, named bluntly. Example: "We were lied to about productivity, and the lie cost us our twenties."
  • SCENE — a vivid 6-word image of a real moment. Example: "It's 2 a.m. in a Tokyo love hotel and my translator just quit."
  • DATA REVELATION — one specific number that reframes the topic. Example: "70% of Gen-Z creators won't make a dollar this year. Here's why the other 30% will."
  • DIRECT QUESTION — sharp, personal, not rhetorical. Example: "What if the people you envy are the most miserable people you know?"
  • INSIDER ADMISSION — name-drop authority, then reveal what they don't say in public. Example: "I sat with three Pulitzer winners last week. None of them write the way they tell you to."

HARD RULES (a hook is rejected if it breaks any):
  - NO em-dash openers, NO "imagine", NO "picture this", NO "what if I told you"
  - NO host names, NO show names, NO "you're listening to"
  - NO emojis, NO quotation marks wrapping the hook
  - Sounds spoken, not written. Read each hook aloud — if it has tongue-twisting commas, rewrite
  - Concrete nouns. Real numbers. Real places. Specifics > abstractions

Return ONLY valid JSON, no markdown, no commentary:
{ "hooks": [ { "text": "..." }, { "text": "..." }, { "text": "..." } ] }`;

export const TITLE_PROMPT = (topic, audience, hook) => `You are writing 3 episode TITLE options that match the cold-open hook the host already chose. Titles are what shows up on Apple Podcasts, Spotify, and YouTube — they have to land in 2 seconds of glance time.

TOPIC: ${topic}
AUDIENCE & ANGLE: ${audience || '(general curious listener)'}
SELECTED HOOK: "${hook}"

Write 3 titles. Each 3-9 words. Each must feel like it BELONGS with the hook above — same energy, same promise. Pull a noun or a verb from the hook to anchor.

Pick three distinct shapes — never reuse:
  • DECLARATIVE — a statement of fact that's surprising. "Most Founders Are Lonely on Purpose"
  • NUMBERED — a count, age, year, or %. "The 4-Year Burnout Cycle Nobody Talks About"
  • POSESSIVE / NAMED — a person, place, or thing as the subject. "What David Lynch Knew About Boredom"
  • CONTRARIAN — explicitly inverts a common belief. "Quit While You're Winning"
  • QUESTION — sharp, specific, personal. "Are You Building a Life or a Resume?"

HARD RULES:
  - Title Case (capitalize main words; lowercase a/an/the/and/or/of/in/to/for unless they start the title)
  - NO colons unless they genuinely add a beat ("Big Idea: Tiny Reason" is fine; "Episode 1: A Story" is not)
  - NO emojis, NO quotes around the title, NO trailing periods
  - Specific > clever. "How Sponsorship Decks Lie" beats "The Ad Game Unmasked"
  - Sounds like something a smart friend would text you, not a course title

Return ONLY valid JSON:
{ "titles": [ { "text": "..." }, { "text": "..." }, { "text": "..." } ] }`;

// ── Helpers ────────────────────────────────────────────────────────────

// Escape raw control characters (newlines, tabs, etc.) that appear
// INSIDE JSON string literals. Long-form beat content routinely comes
// back with literal newlines inside the "content" value — which is
// invalid JSON and makes JSON.parse throw, even though the data is
// perfectly good. We walk the string tracking whether we're inside a
// string literal and escape only the control chars that land there;
// structural whitespace outside strings is left untouched.
function escapeControlCharsInStrings(s) {
  let out = '';
  let inStr = false;
  let escaped = false;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    const code = s.charCodeAt(i);
    if (inStr) {
      if (escaped) { out += ch; escaped = false; continue; }
      if (ch === '\\') { out += ch; escaped = true; continue; }
      if (ch === '"') { out += ch; inStr = false; continue; }
      if (code < 0x20) {
        out += ch === '\n' ? '\\n'
             : ch === '\r' ? '\\r'
             : ch === '\t' ? '\\t'
             : '\\u' + code.toString(16).padStart(4, '0');
        continue;
      }
      out += ch;
    } else {
      if (ch === '"') { inStr = true; }
      out += ch;
    }
  }
  return out;
}

// Tiny safe-JSON helper — Claude sometimes wraps its JSON in fences
// despite instructions. Strip leading/trailing junk, then parse; if
// that fails (almost always unescaped newlines in long string values)
// retry once with control chars escaped inside string literals.
export function parseLLMJson(raw) {
  if (!raw) return null;
  let s = String(raw).trim();
  s = s.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/, '');
  const first = s.indexOf('{');
  const last  = s.lastIndexOf('}');
  if (first >= 0 && last > first) s = s.slice(first, last + 1);
  try { return JSON.parse(s); } catch {}
  try { return JSON.parse(escapeControlCharsInStrings(s)); } catch { return null; }
}

// /api/claude wrapper. Default model is whatever modelConfig says
// 'generation' should be (Sonnet today; will be swappable to higher
// tiers later via the version-tier abstraction). Callers can override
// per-call if they want.
//
// Always streams. The non-streaming path transferred zero bytes to the
// client until the entire generation finished (20–45s for a Sonnet
// build call). Mobile Safari and cellular carrier proxies kill an idle
// connection long before that — so on phones the clip / episode build
// reliably died mid-flight and surfaced as "hit a glitch mid-build,"
// while desktop Chrome (which tolerates the long silent wait) was fine.
// Streaming keeps SSE bytes flowing token-by-token, which keeps the
// socket alive past those mobile timeouts. The server accumulates the
// full text and emits a final { done, text }; we return that, so every
// caller's contract (prompt in → text out) is unchanged.
//
// opts (optional):
//   system     — system-level instructions (voice rules, persona).
//                Goes into Anthropic's `system` field, not the user
//                message. Cleaner separation + Anthropic gives it
//                proper weight.
//   max_tokens — output token cap. Default 1200.
//   signal     — optional AbortSignal; forwarded to fetch so a parent
//                operation (e.g. user clicks Stop) can cancel the call.
export async function askClaude(prompt, model = modelFor('generation'), opts = {}) {
  const body = {
    model,
    max_tokens: opts.max_tokens || 1200,
    messages: [{ role: 'user', content: prompt }],
    stream: true,
  };
  if (opts.system) body.system = opts.system;

  // Mobile cellular is the hostile case here: the architect fires many
  // of these SSE streams in parallel, and carrier proxies love to drop a
  // long-lived connection mid-body. A single dropped stream used to kill
  // the whole build (Promise.all rejects). So we retry transient network
  // failures — whether they hit on the initial fetch OR mid-stream while
  // reading — with a short backoff. Real API errors (bad key, rate
  // limit, an error inside the stream) and user aborts are NOT retried.
  const MAX_ATTEMPTS = 3;
  let lastErr;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    try {
      return await streamClaudeOnce(body, opts.signal);
    } catch (err) {
      lastErr = err;
      // User-initiated cancel — never retry, surface immediately.
      if (err?.name === 'AbortError' || opts.signal?.aborted) throw err;
      // Only transient network drops are worth retrying. Anything else
      // (auth, rate limit, in-stream Claude error) is deterministic.
      if (!isTransientNetworkError(err) || attempt === MAX_ATTEMPTS) throw err;
      // 400ms, then 800ms. Abort-aware so Stop cancels the wait too.
      await sleepAbortable(400 * attempt, opts.signal);
    }
  }
  throw lastErr;
}

// One fetch + SSE-read cycle. Throws a network error (annotated with
// timing + online flag) on either a failed initial fetch or a mid-stream
// read drop, so the retry layer above can tell those apart from real API
// errors.
async function streamClaudeOnce(body, signal) {
  // Network-level failures on iOS Safari surface as a bare
  // "TypeError: Load failed" with no context. Annotate with timing
  // (instant vs. mid-stream tells very different stories), the offline
  // flag, and WHERE it died so logs distinguish a cold-start abort from
  // a genuine connectivity drop.
  const t0 = Date.now();
  let res;
  try {
    res = await fetch('/api/claude', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal,
    });
  } catch (networkErr) {
    throw annotateNetworkError(networkErr, t0, 'pre-stream');
  }

  // Pre-stream failures (missing API key, malformed request) still come
  // back as a non-200 JSON body before the SSE stream opens.
  if (!res.ok) {
    let detail = '';
    try { detail = (await res.json())?.error || ''; }
    catch { detail = await res.text().catch(() => ''); }
    throw new Error(`Claude error ${res.status}: ${detail || 'no body'}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let fullText = '';
  let buf = '';

  while (true) {
    let chunk;
    try {
      chunk = await reader.read();
    } catch (readErr) {
      // Connection dropped mid-body — the common mobile failure. Left
      // unwrapped this bubbled up as a raw "TypeError: Load failed".
      throw annotateNetworkError(readErr, t0, 'mid-stream');
    }
    if (chunk.done) break;

    buf += decoder.decode(chunk.value, { stream: true });
    const lines = buf.split('\n');
    buf = lines.pop(); // keep the incomplete trailing line for next read

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      const payload = line.slice(6).trim();
      if (!payload || payload === '[DONE]') continue;
      let evt;
      try { evt = JSON.parse(payload); }
      catch { continue; } // skip a malformed SSE line, keep streaming
      // In streaming mode the HTTP status is 200 even when Anthropic /
      // the proxy failed — the error rides inside the stream instead.
      if (evt.error) throw new Error(`Claude error: ${evt.error}`);
      if (evt.delta) fullText += evt.delta;
      if (evt.done && typeof evt.text === 'string') fullText = evt.text;
    }
  }

  return fullText;
}

// True for connection-level failures (initial fetch or mid-stream read)
// that are worth retrying — as opposed to deterministic API errors.
// Covers Safari ("Load failed"), Chrome ("Failed to fetch"), and
// Firefox ("NetworkError when attempting to fetch resource").
function isTransientNetworkError(err) {
  if (!err) return false;
  if (err.name === 'TypeError') return true;
  const m = String(err.message || '').toLowerCase();
  return m.includes('load failed')
      || m.includes('failed to fetch')
      || m.includes('network')
      || m.includes('connection');
}

function annotateNetworkError(err, t0, where) {
  const ms = Date.now() - t0;
  const online = typeof navigator !== 'undefined' ? navigator.onLine : 'unknown';
  const tag = `[/api/claude ${where} after ${ms}ms · online=${online}]`;
  const wrapped = new Error(`${err.name || 'NetworkError'}: ${err.message || 'unknown'} ${tag}`);
  wrapped.name = err.name || 'NetworkError';
  return wrapped;
}

// Abort-aware delay: resolves after `ms`, or rejects with an AbortError
// the moment `signal` fires — so a user hitting Stop during a retry
// backoff cancels promptly instead of waiting out the timer.
function sleepAbortable(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      const e = new Error('Aborted'); e.name = 'AbortError'; reject(e); return;
    }
    const id = setTimeout(() => {
      signal?.removeEventListener?.('abort', onAbort);
      resolve();
    }, ms);
    function onAbort() {
      clearTimeout(id);
      const e = new Error('Aborted'); e.name = 'AbortError'; reject(e);
    }
    signal?.addEventListener?.('abort', onAbort, { once: true });
  });
}

// Local-only fallbacks if /api/claude isn't reachable. Behavior here
// is preserved verbatim from the legacy LandingChatFlow.js. Note:
// these are visibly worse than real Claude output — flagged as a known
// degradation surface in LANDING_CHAT_CONTRACT.md §6.6.
export function fallbackHooks(topic) {
  return [
    { text: `Everyone says ${topic}. Almost no one does it. Here's the gap — and how we close it this week.` },
    { text: `What if ${topic} is the wrong question? The right one is the one nobody's asking.` },
    { text: `One conversation about ${topic} changed how the next ten years play out. We rewind the moment.` },
  ];
}

export function fallbackTitles(topic) {
  return [
    { text: `The Truth About ${topic}` },
    { text: `Inside ${topic}: What No One Tells You` },
    { text: `Rethinking ${topic}` },
  ];
}
